import { escapeODataLiteral } from "../shared/odata";
import {
  BcOperationalOrder,
  fetchOperationalOrdersByNo,
  fetchOperationalOrderByNo,
  fetchOperationalOrderByInternalReference,
  fetchOrdersUrlByOrderNos,
  checkLinkedDocumentExists,
} from "./bc-api";
import type { BcRequestContext } from "../shared/auth-state";

export async function searchOperationalOrders(
  query: string,
  bc: BcRequestContext,
  options?: { signal?: AbortSignal },
): Promise<BcOperationalOrder[]> {
  const no_ = query.trim();
  if (!no_) return [];
  const safe = escapeODataLiteral(no_);
  return fetchOperationalOrdersByNo(safe, bc, { signal: options?.signal });
}

/**
 * Looks up an operational order by its exact order number (e.g. an acronym-prefixed
 * candidate word extracted from an email subject). Used for auto-selecting an order --
 * see autoSelectOrdersFromEmailSubjects in customers.ts.
 */
export async function findOperationalOrderByNo(
  orderNo: string,
  bc: BcRequestContext,
  options?: { signal?: AbortSignal },
): Promise<BcOperationalOrder[]> {
  const no_ = orderNo.trim();
  if (!no_) return [];
  const safe = escapeODataLiteral(no_);
  return fetchOperationalOrderByNo(safe, bc, { signal: options?.signal });
}

/**
 * Looks up an operational order by its exact internalReference (e.g. an
 * internalReference-shaped candidate word extracted from an email subject). Used as the
 * auto-select fallback when no order-number candidate matches -- see
 * autoSelectOrdersFromEmailSubjects in customers.ts.
 */
export async function findOperationalOrderByInternalReference(
  internalReference: string,
  bc: BcRequestContext,
  options?: { signal?: AbortSignal },
): Promise<BcOperationalOrder[]> {
  const ref = internalReference.trim();
  if (!ref) return [];
  const safe = escapeODataLiteral(ref);
  return fetchOperationalOrderByInternalReference(safe, bc, { signal: options?.signal });
}

/**
 * Looks up the shared "ordersURL" link for a set of orders, used to make the post-upload
 * status banner clickable -- see uploadOnly in customers.ts.
 */
export async function findOrdersUrlForOrderNos(
  orderNos: string[],
  bc: BcRequestContext,
  options?: { signal?: AbortSignal },
): Promise<string | null> {
  const safe = orderNos.map((no) => no.trim()).filter(Boolean).map(escapeODataLiteral);
  if (safe.length === 0) return null;
  return fetchOrdersUrlByOrderNos(safe, bc, { signal: options?.signal });
}

/**
 * Whether fileName -- optionally scoped to a single orderNo -- is already linked to an
 * order in BC. Live query, not session memory, so it reflects reality even after a page
 * reload -- see refreshUploadedBadges in attachments.ts.
 */
export async function isFileAlreadyLinked(
  fileName: string,
  orderNo: string | null,
  bc: BcRequestContext,
  options?: { signal?: AbortSignal },
): Promise<boolean> {
  const name = fileName.trim();
  if (!name) return false;
  const safeFileName = escapeODataLiteral(name);
  const trimmedOrderNo = orderNo?.trim();
  const safeOrderNo = trimmedOrderNo ? escapeODataLiteral(trimmedOrderNo) : null;
  return checkLinkedDocumentExists(safeFileName, safeOrderNo, bc, { signal: options?.signal });
}
